// src/screens/AuthScreen.js
import React, { useRef, useContext, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Box, Tabs, Tab, Grid, Typography, TextField, Tooltip } from "@mui/material";
import Login from "./Login";
import { DarkSurface } from "../ui";
import SignUp from "./SignUp";
import { MainContext } from "../../App";
import NeoButton from "../NeoButton";
import InfinityOrb from "../InfinityOrb";
import NodeDash from "../NodeDash";
import { db } from "../../@shared/firebase/config";
import { Close } from "@mui/icons-material";
import NavBar from "../NavBar";
import FeaturedMerchStrip from "./FeaturedMerchStrip";
import FeatureCollectionModal from "../FeatureCollectionModal";
import { useNavigate } from "react-router-dom";
import { HeaderCard, useEnrollmentLeaderboardLive } from "../CollectionEnrollmentPage";


const Landing = ({ isAuth }) => {
  const section1Ref = useRef(null);
  const scrollRef = useRef(null);
  const { Theme, setSuccess, setError, setMessage, deviceType } =
    useContext(MainContext);
  const [activeTab, setActiveTab] = useState(isAuth ? 0 : 1);
  const [cardsRevealed, setCardsRevealed] = useState(false);
  const [email, setEmail] = React.useState("");
  const [showModal, setShowModal] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);
  const navigate = useNavigate();

    const { loading: topLoading, items: topCollections } =
    useEnrollmentLeaderboardLive({ limitCount: 3 });


  const featureItems = [
    {
      id: "eligibility",
      icon: require("../../@shared/assets/icons/star_icon.png"),
      title: "Eligibility",
      body: "Active community, verifiable metadata, transparent roadmap."
    },
    {
      id: "benefits",
      icon: require("../../@shared/assets/icons/quest_scroll_icon.png"),
      title: "Benefits",
    },
    {
      id: "apply",
      icon: require("../../@shared/assets/icons/rocket_icon.png"),
      title: "How to apply",
      body: "Share links, chain(s), supply, notable collabs, and plans."
    },
  ];

  const footerLinks = [
    { label: "About" },
    { label: "Roadmap" },
    { label: "FAQ" },
    { label: "Support" },
    { label: "Terms", path: "/policy?tab=terms" },
    { label: "Privacy", path: "/policy?tab=privacy" },
  ];

  const handleAddToWaitlist = async () => {
    try {
      const snapshot = await db
        .collection("waitlist")
        .where("email", "==", email)
        .get();
      if (!snapshot.empty) {
        setError(true);
        setMessage("You are already on the waitlist!");
      } else {
        await db.collection("waitlist").add({
          email: email,
        });
        setEmail("");
        setSuccess(true);
        setMessage("You have been added to the waitlist!");
      }
    } catch (error) {
      setError(true);
      setMessage("There was an error adding you to the waitlist.");
    }
  };

  const isInView = useInView(section1Ref, { once: true, margin: "-20% 0px" });

  const { scrollYProgress } = useScroll({
    container: scrollRef,
  });

  // Map scroll progress to bottom position of the gif
  const bottomPosition = useTransform(
    scrollYProgress,
    [0.15, 0.3],
    [
      deviceType === "desktop" ? "-30%" : "-20%",
      deviceType === "desktop" ? "-134%" : "-20%",
    ]
  );
  const starTranslate = useTransform(
    scrollYProgress,
    [0.15, 0.3],
    ["0%", "124%"]
  );
  const starScale = useTransform(scrollYProgress, [0.25, 0.3], [1, 0.75]);

  const cardVariants = {
    hidden: {
      opacity: 0,
      scale: 0.25,
      rotate: 0,
      x: 0,
    },
    visible: (index) => ({
      opacity: 1,
      scale: 1,
      rotate: [-20, 0, 20][index], // fan effect
      x: deviceType === "desktop" ? [-80, 0, 80][index] : [-40, 0, 40][index],
      y: [20, -10, 20][index],
      transition: {
        duration: 0.8,
        delay: 0.5 + index * 0.2,
      },
    }),
  };

  // Character slide-out animation
  const characterVariants = {
    hidden: (side) => ({
      opacity: 0,
      x: 0,
      transform: side === "left" && "scaleX(-1)",
    }),
    visible: (side) => ({
      opacity: 1,
      x: side === "left" ? -160 : deviceType === "desktop" ? 160 : 300,
      transform: side === "left" && "translate(-0%, -24%) scaleX(-1)",
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.5,
      },
    }),
  };

  // Trigger card animation completion
  useEffect(() => {
    if (isInView) {
      setTimeout(() => setCardsRevealed(true), 1000); // Delay slightly longer than total card reveal
    }
  }, [isInView]);

  // Debugging scroll progress
  useEffect(() => {
    return scrollYProgress.onChange((v) => {
      console.log("scrollYProgress:", v);
    });
  }, [scrollYProgress]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
      {isAuth ? (
        <Grid
          item
          container
          xs={12}
          style={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab label="Login" />
              <Tab label="Sign Up" />
            </Tabs>
            {activeTab === 0 && <Login />}
            {activeTab === 1 && <SignUp />}
          </motion.div>
        </Grid>
      ) : (
        <>
          <motion.div
            ref={scrollRef}
            style={{
              height: "100%",
              overflowY: "scroll",
              overflowX: "hidden",
              background: "linear-gradient(to bottom, #230a67, #5c0c9e)",
              position: "relative",
              scrollBehavior: "smooth",
              width: "100%",
            }}
          >
            {/* NAVIGATION BAR */}
            {/* <NavBar/> */}

            {/* HEADER - HERO */}
            <Grid item xs={12} sx={{ height: "100%", position: "relative" }}>
              <InfinityOrb />
              <NodeDash
                cardImages={[require("../../@shared/assets/card-dash.png")]}
                itemImages={[
                  require("../../@shared/assets/infinityCards/ironclaw_gloves.png"),
                  require("../../@shared/assets/infinityCards/lifebloom_potion.png"),
                  require("../../@shared/assets/infinityCards/herbal_tonic.png"),
                  require("../../@shared/assets/infinityCards/pitfall_token.png"),
                  require("../../@shared/assets/infinityCards/minor_healing_potion.png"),
                  require("../../@shared/assets/infinityCards/boneblade_fragment.png"),
                  require("../../@shared/assets/infinityCards/pulse_of_life.png"),
                  require("../../@shared/assets/infinityCards/venom_tipped_blade.png"),
                  require("../../@shared/assets/infinityCards/crimson_edge_shard.png"),
                  require("../../@shared/assets/infinityCards/crystal_armor_shard.png"),
                  require("../../@shared/assets/infinityCards/ghoststeel_spike.png"),
                ]}
                spawnRate={500}
              //   lifetime={500}
              />
              <motion.img
                initial={{
                  opacity: 0,
                  top: deviceType === "desktop" ? "40%" : "20%",
                }}
                animate={{
                  opacity: 1,
                  top: deviceType === "desktop" ? "30%" : "30%",
                }}
                transition={{ duration: 1.5 }}
                src={require("../../@shared/assets/logo-icon-text-reg.png")}
                style={{
                  height: "auto",
                  width: deviceType === "desktop" ? "30%" : "50%",
                  position: "absolute",
                  left: "50%",
                  top: "30%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                }}
              />

              <motion.div
                initial={{ opacity: 0, top: "63%" }}
                animate={{ opacity: 1, top: "53%" }}
                transition={{ duration: 1.5, delay: 0.75 }}
                style={{
                  position: "absolute",
                  top: "53%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  zIndex: 3,
                  width: "80%",
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    color: "white",
                    fontSize: { xs: 20, md: 30 },
                    fontWeight: 700,
                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                    textAlign: "center",
                  }}
                >
                  The Ultimate NFT Showdown.
                </Typography>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    color: "white",
                    fontSize: { xs: 14, md: 20 },
                    fontWeight: 400,
                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                    textAlign: "center",
                  }}
                >
                  Battle, Collect, Earn, and Dominate!
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, top: "75%" }}
                animate={{ opacity: 1, top: "70%" }}
                transition={{ duration: 1.5, delay: 0.75 }}
                style={{
                  position: "absolute",
                  top: "53%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  zIndex: 3,
                  width: deviceType === "desktop" ? "40%" : "90%",
                }}
              >
                <NeoButton
                  color={Theme.palette.primary.main}
                  text="Play Now"
                  width={50}
                  onClick={() => setShowModal(true)}
                />
                <Box sx={{ mt: 1, width: "100%", display: "flex", justifyContent: "center" }}>
                  <NeoButton
                    color="#00aaff"
                    text="How To Play"
                    width={50}
                    onClick={() => navigate("/how-to-play")}
                  />
                </Box>
                {/* NEW: Feature CTA under Play Now */}
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/onboarding")}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/onboarding")}
                  sx={{
                    mt: 1,
                    color: "#fff",
                    textAlign: "center",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 700,
                    letterSpacing: 0.25,
                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`
                  }}
                >
                  Want your collection featured in game? Click here.
                </Typography>
              </motion.div>

              <Box
                sx={{
                  position: "sticky",
                  top: "2%",
                  left: "calc(100% - 76px)",
                  zIndex: 900,
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '10px',
                  background: Theme.palette.tirciary.main,
                  borderRadius: '8px 0px 0px 8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 'fit-content'
                }}
              >
                <motion.div whileHover={{ scale: 1.1, opacity: .75 }} whileTap={{ scale: .95 }} >
                  <Tooltip title='Join out Discord!'>
                    <img src={require('../../@shared/assets/icons/discord.webp')} style={{ width: '32px', height: '32px', marginRight: 8, cursor: 'pointer' }} onClick={() => { window.open('https://t.co/1jigfXsyEi') }} />
                  </Tooltip>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, opacity: .75 }} whileTap={{ scale: .95 }} >
                  <Tooltip title='Follow us on X!'>
                    <img src={require('../../@shared/assets/icons/x.png')} style={{ width: '32px', height: '32px', marginRight: 8, cursor: 'pointer' }} onClick={() => { window.open('https://x.com/Infinftroyale') }} />

                  </Tooltip>
                </motion.div>

              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: "2%",
                  left: "2%",
                  zIndex: 900,
                }}
              >
                <img
                  style={{
                    height: 50,
                    width: "auto",
                  }}
                  src={require("../../@shared/assets/avail-on.png")}
                />
              </Box>
            </Grid>

            {/* INFO SECTION 1 */}
            <Grid
              ref={section1Ref}
              container
              item
              xs={12}
              sx={{
                height: deviceType === "desktop" ? "100vh" : "60vh",
                alignItems: deviceType === "desktop" ? "center" : "flex-end",
                justifyContent: "center",
                color: "#fff",
                fontSize: "2rem",
                position: "relative",
              }}
            >
              <motion.img
                src={require("../../@shared/assets/red-wizard-start.png")}
                variants={characterVariants}
                initial="hidden"
                animate={cardsRevealed ? "visible" : "hidden"}
                custom="left"
                style={{
                  height: deviceType === "desktop" ? "36%" : "60%",
                  width: "auto",
                  position: "absolute",
                  left: deviceType === "desktop" ? "22%" : "-20%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1,
                }}
              />
              {new Array(3).fill(0).map((_, index) => (
                <motion.img
                  key={index}
                  src={require("../../@shared/assets/back-of-card.png")}
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  style={{
                    height: deviceType === "desktop" ? "40%" : "30%",
                    width: "auto",
                    position: "absolute",
                    left: "42%",
                    top: "24%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 100,
                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
                  }}
                />
              ))}
              <motion.img
                src={require("../../@shared/assets/nox-start.png")}
                variants={characterVariants}
                initial="hidden"
                animate={cardsRevealed ? "visible" : "hidden"}
                custom="right"
                style={{
                  height: deviceType === "desktop" ? "40%" : "64%",
                  width: "auto",
                  position: "absolute",
                  right: "28%",
                  top: "20%",
                  transform: "translate(50%, -50%)",
                  zIndex: 1,
                }}
              />
              <motion.div
                initial="hidden"
                animate={cardsRevealed ? "visible" : "hidden"}
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.25,
                      delayChildren: 0.6, // after characters
                    },
                  },
                }}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -30%)",
                  textAlign: "center",
                  zIndex: 200,
                  width: "100%",
                }}
              >
                {["CARD SLAPS.", "SPELL BLASTS.", "TOTAL CHAOS."].map(
                  (line, i) => (
                    <motion.div
                      key={i}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.6 },
                        },
                      }}
                      style={{
                        fontSize: deviceType === "desktop" ? "7rem" : "2.5rem",
                        fontWeight: 800,
                        color: Theme.palette.text.main,
                        // WebkitTextStroke: `6px #230a67`, // black outline
                        textShadow: ` -5px -5px 0 #230a67, 5px -5px 0 #230a67, -5px  5px 0 #230a67, 5px  5px 0 #230a67`,
                        lineHeight: 0.8,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {line}
                    </motion.div>
                  )
                )}
              </motion.div>
            </Grid>

            {/* INFO SECTION 2 */}
            <Grid
              container
              item
              xs={12}
              sx={{
                height: { sx: "100dvh", md: "80dvh" },
                justifyContent: "center",
                background: "linear-gradient(to top, #200662, #2f0b7300)",
                position: "relative",
                px: deviceType === "desktop" ? 6 : 1,
              }}
            >
              <Grid
                item
                container
                xs={12}
                md={10}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#fff",
                  fontSize: "2rem",
                }}
              >
                <motion.img
                  initial={{ opacity: 0, x: -100 }}
                  whileInView={{
                    opacity: 1,
                    x: deviceType === "desktop" ? 0 : 50,
                  }}
                  transition={{ duration: 1 }}
                  src={require("../../@shared/assets/preview-walk-around.gif")}
                  style={{
                    height: "80%",
                    width: deviceType === "desktop" ? "auto" : "60%",
                    rotate: "-10deg",
                    zIndex: 1,
                  }}
                />

                {deviceType === "desktop" && (
                  <motion.img
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: -200 }}
                    transition={{ duration: 1 }}
                    src={require("../../@shared/assets/back-of-card.png")}
                    style={{
                      height: "50%",
                      width: deviceType === "desktop" ? "auto" : "80%",
                      rotate: "10deg",
                      zIndex: 0,
                    }}
                  />
                )}

                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  style={{
                    maxWidth: deviceType === "desktop" ? "40%" : "98%",
                    textAlign: "left",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: Theme.palette.text.main,
                      fontSize: "3rem",
                      WebkitTextStroke: "2px #230a67",
                      textShadow: ` -2px -2px 0 #230a67, 2px -2px 0 #230a67, -2px  2px 0 #230a67, 2px  2px 0 #230a67`,
                      mb: 2,
                    }}
                  >
                    Explore. Build. Strategize.
                  </Typography>
                  <Typography sx={{ fontSize: 18, lineHeight: 1.6 }}>
                    Travel dynamic maps and unlock unique cards to customize
                    your deck. Dive into matches with your squad or solo and
                    prepare for chaos. Every move matters. Every card counts.
                  </Typography>
                </motion.div>
              </Grid>
            </Grid>

            {/* INFO SECTION 3 */}
            <Grid
              container
              item
              xs={12}
              sx={{
                height: { sx: "100dvh", md: "80dvh" },
                justifyContent: "center",
                background: "#210663",
                position: "relative",
                px: deviceType === "desktop" ? 6 : 1,
              }}
            >
              <Grid
                item
                container
                xs={12}
                md={10}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#fff",
                  fontSize: "2rem",
                }}
              >
                {deviceType === "desktop" && (
                  <motion.div
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    style={{
                      maxWidth: "40%",
                      textAlign: "left",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: Theme.palette.text.main,
                        fontSize: "3rem",
                        WebkitTextStroke: "2px #230a67",
                        textShadow: ` -2px -2px 0 #230a67, 2px -2px 0 #230a67, -2px  2px 0 #230a67, 2px  2px 0 #230a67`,
                        mb: 2,
                      }}
                    >
                      Battle to the Top.
                    </Typography>
                    <Typography sx={{ fontSize: 18, lineHeight: 1.6 }}>
                      Engage in tactical battles, unleash your strongest combos,
                      and climb the global leaderboard. Prove your dominance in
                      every season and become a legend.
                    </Typography>
                  </motion.div>
                )}

                {deviceType === "desktop" && (
                  <motion.img
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 100 }}
                    transition={{ duration: 1 }}
                    src={require("../../@shared/assets/back-of-card.png")}
                    style={{
                      height: "50%",
                      width: "auto",
                      rotate: "-10deg",
                      zIndex: 0,
                    }}
                  />
                )}

                <motion.img
                  initial={{ opacity: 0, x: 0 }}
                  whileInView={{
                    opacity: 1,
                    x: deviceType === "desktop" ? -100 : 50,
                  }}
                  transition={{ duration: 1 }}
                  src={require("../../@shared/assets/preview-battle.gif")}
                  style={{
                    height: "80%",
                    width: deviceType === "desktop" ? "auto" : "60%",
                    rotate: "10deg",
                    zIndex: 1,
                  }}
                />

                {deviceType === "mobile" && (
                  <motion.div
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    style={{
                      maxWidth: "100%",
                      textAlign: "left",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: Theme.palette.text.main,
                        fontSize: "3rem",
                        WebkitTextStroke: "2px #230a67",
                        textShadow: ` -2px -2px 0 #230a67, 2px -2px 0 #230a67, -2px  2px 0 #230a67, 2px  2px 0 #230a67`,
                        mb: 2,
                      }}
                    >
                      Battle to the Top.
                    </Typography>
                    <Typography sx={{ fontSize: 18, lineHeight: 1.6 }}>
                      Engage in tactical battles, unleash your strongest combos,
                      and climb the global leaderboard. Prove your dominance in
                      every season and become a legend.
                    </Typography>
                  </motion.div>
                )}
              </Grid>
            </Grid>

                        {/* TOP ENROLLING COLLECTIONS SECTION */}
            <Grid
              container
              item
              xs={12}
              sx={{
                background: "#190545",
                justifyContent: "center",
                px: deviceType === "desktop" ? 6 : 1,
                py: deviceType === "desktop" ? 4 : 3,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 1200,
                  mx: "auto",
                }}
              >
                <Box sx={{width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: Theme.palette.text.main,
                      fontSize: deviceType === "desktop" ? "2.25rem" : "1.75rem",
                      WebkitTextStroke: "1px #230a67",
                      textShadow:
                        "-2px -2px 0 #230a67, 2px -2px 0 #230a67, -2px 2px 0 #230a67, 2px 2px 0 #230a67",
                      mb: 1,
                    }}
                  >
                    Top Enrolling Collections
                  </Typography>

                  {/* View more button - top left */}
                  <Box sx={{ mb: 3, width:'50%', display:'flex', justifyContent:'flex-end' }}>
                    <NeoButton
                      text="View all >"
                      color={Theme.palette.primary.main}
                      width={40}
                      height="36px"
                      onClick={() => navigate("/enrollment")}
                    />
                  </Box>

                </Box>

                {topLoading ? (
                  <Grid container spacing={2}>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Grid item xs={12} md={4} key={idx}>
                        <Box
                          sx={{
                            height: 220,
                            borderRadius: 2,
                            bgcolor: "#231543",
                            border: (t) =>
                              `1px solid ${t.palette.tirciary.light}`,
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : topCollections.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "#c9b7ff", opacity: 0.85 }}
                  >
                    No collections are currently enrolled yet. Check back soon!
                  </Typography>
                ) : (
                  <Grid container spacing={3}>
                    {topCollections.map((col) => (
                      <Grid item xs={12} md={4} key={col.id}>
                        {/* Reuse the enrollment header styling */}
                        <HeaderCard collection={col} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>


            {/* MERCH SECTION */}
            {/* <FeaturedMerchStrip /> */}

            {/* INFO SECTION 4 */}

            <Grid
              container
              item
              xs={12}
              sx={{
                height: "100vh",
                backgroundImage: `url(${require("../../@shared/assets/city-wide-landing.png")})`,
                backgroundSize: "cover",
                backgroundPosition: "center top",
                backgroundRepeat: "no-repeat",
                color: "#fff",
                overflow: "hidden",
                justifyContent: "center",
              }}
            >
              <Grid
                container
                item
                xs={12}
                md={10}
                sx={{ height: "100%", position: "relative" }}
              >
                {/* Left Characters (Perspective Overlap) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: deviceType === "desktop" ? "20%" : "-5%",
                    bottom: "5%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    alignItems: "flex-start",
                    gap: -20,
                    height: "100%",
                  }}
                >
                  {[
                    {
                      img: require("../../@shared/assets/toshi-start.png"),
                      height: "40%",
                    },
                    {
                      img: require("../../@shared/assets/blitz-start.png"),
                      height: "50%",
                    },
                    {
                      img: require("../../@shared/assets/azukii.png"),
                      height: "50%",
                    },
                  ].map((_, i) => {
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.2 }}
                        style={{
                          width: "fit-content",
                          height: _.height,
                          borderRadius: 8,
                          marginBottom: `-${i === 2 ? 10 : 90}%`,
                          marginLeft:
                            deviceType === "desktop"
                              ? `-${i * 35}%`
                              : `-${i * 10}%`,
                          zIndex: i, // preserve layer stacking
                        }}
                      >
                        <img
                          src={_.img}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </motion.div>
                    );
                  })}
                </Box>

                {/* Right Characters (Perspective Overlap) */}
                <Box
                  sx={{
                    position: "absolute",
                    right: deviceType === "desktop" ? "15%" : "-10%",
                    bottom: deviceType === "desktop" ? 0 : "3%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    height: "100%",
                    gap: -20,
                  }}
                >
                  {[
                    {
                      img: require("../../@shared/assets/frog_attack.png"),
                      height: "45%",
                    },
                    {
                      img: require("../../@shared/assets/big.png"),
                      height: "50%",
                    },
                    {
                      img: require("../../@shared/assets/obscurio.png"),
                      height: "50%",
                    },
                  ].map((_, i) => {
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.2 }}
                        style={{
                          width: "fit-content",
                          height: _.height,
                          borderRadius: 8,
                          marginBottom: `-${i === 2 ? 10 : 100}%`,
                          marginRight:
                            deviceType === "desktop"
                              ? `-${i * 30}%`
                              : `-${i * 10}%`,
                          zIndex: i,
                        }}
                      >
                        <img
                          src={_.img}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </motion.div>
                    );
                  })}
                </Box>

                {/* Center Callout */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  style={{
                    position: "absolute",
                    top: deviceType === "desktop" ? "45%" : "10%",
                    left: "50%",
                    transform: "translate(-50%, 0%)",
                    textAlign: "center",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: Theme.palette.text.main,
                      fontSize: "3rem",
                      WebkitTextStroke: "2px #230a67",
                      textShadow: ` -2px -2px 0 #230a67, 2px -2px 0 #230a67, -2px  2px 0 #230a67, 2px  2px 0 #230a67`,
                    }}
                  >
                    Bring Your NFT to Battle.
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: "white",
                      fontSize: "1.2rem",
                      mt: 1,
                      textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                    }}
                  >
                    Compete, Earn, and Dominate the digital arena.
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, top: "75%" }}
                  animate={{ opacity: 1, top: "70%" }}
                  transition={{ duration: 1.5, delay: 0.75 }}
                  style={{
                    position: "absolute",
                    top: "53%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    zIndex: 3,
                    width: deviceType === "desktop" ? "40%" : "100%",
                  }}
                >
                  <NeoButton
                    color={Theme.palette.primary.main}
                    text="Battle Now!"
                    width={50}
                    onClick={() => setShowModal(true)}
                  />
                </motion.div>
              </Grid>
              {/* Left Opponents (Perspective Overlap) */}
            </Grid>

            <motion.img
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
              src={require("../../@shared/assets/spacey-stars.png")}
              style={{
                height: "auto",
                width: "40%",
                position: "absolute",
                left: "-15%",
                top: "-10%",
                zIndex: 2,
                translateY: starTranslate,
                scale: starScale,
              }}
            />

            <motion.img
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
              src={require("../../@shared/assets/spacey-stars.png")}
              style={{
                height: "auto",
                width: "38%",
                position: "absolute",
                bottom: "-20%",
                right: "-10%",
                zIndex: 2,
                translateY: starTranslate,
                scale: starScale,
              }}
            />

            {/* Animated GIF */}
            <motion.img
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, delay: 1.5 }}
              src={require("../../@shared/assets/galaxy.gif")}
              style={{
                height: "auto",
                width: deviceType === "desktop" ? "40%" : "100%",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: bottomPosition,
                zIndex: 1,
              }}
            />

            {showModal && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 999,
                }}
              >
                <motion.div
                  initial={{ y: -30 }}
                  animate={{ y: 0 }}
                  exit={{ y: -30 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    background:
                      "radial-gradient(120% 70% at 50% -10%, rgba(140,108,255,0.3) 0%, rgba(140,108,255,0) 60%), linear-gradient(180deg, rgba(34,20,74,0.96) 0%, rgba(14,7,32,0.98) 100%)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    padding: "2rem",
                    borderRadius: 20,
                    width: "90%",
                    maxWidth: deviceType === "desktop" ? 400 : 320,
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff",
                    boxShadow:
                      "0 24px 60px rgba(0,0,0,0.6), 0 0 60px rgba(124,92,255,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-12%",
                      right: "-8%",
                      padding: "1rem",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close style={{ color: "white" }} />}
                      onClick={() => {
                        setShowModal(false);
                        setEmail("");
                      }}
                      color={Theme.palette.secondary.main}
                    />
                  </div>

                  <DarkSurface>
                    <Tabs value={activeTab} onChange={handleTabChange} centered>
                      <Tab label="Login" />
                      <Tab label="Sign Up" />
                    </Tabs>
                    {activeTab === 0 && <Login />}
                    {activeTab === 1 && <SignUp />}
                  </DarkSurface>
                </motion.div>
              </motion.div>
            )}

            <FeatureCollectionModal
              open={featureOpen}
              onClose={() => setFeatureOpen(false)}
              items={featureItems}
            />

            {/* FOOTER */}
            {/* FOOTER */}
            <motion.footer
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{
                width: "100%",
                backgroundColor: "#0e0326",
                padding: "3rem 1.5rem",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                zIndex: 5,
              }}
            >
              <img
                src={require("../../@shared/assets/logo-icon-text-reg.png")}
                alt="Logo"
                style={{ height: 75, marginBottom: "1rem" }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                {footerLinks.map(
                  ({ label, path }) => (
                    <Typography
                      key={label}
                      component={path ? "button" : "span"}
                      onClick={path ? () => navigate(path) : undefined}
                      sx={{
                        fontSize: 14,
                        color: "#aaa",
                        cursor: path ? "pointer" : "default",
                        border: 0,
                        p: 0,
                        bgcolor: "transparent",
                        fontFamily: "inherit",
                        "&:hover": {
                          color: path ? "#fff" : "#aaa",
                        },
                      }}
                    >
                      {label}
                    </Typography>
                  )
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{ fontSize: 12, color: "#888", mt: 2 }}
              >
                © {new Date().getFullYear()} InfiNFT Royale. All rights
                reserved.
              </Typography>
            </motion.footer>
          </motion.div>
        </>
      )}
    </>
  );
};

export default Landing;
